import React, { useState, useRef, useCallback, useEffect } from 'react';
import "../../style/Camera.css";
import NoSaveCameraSVG from "./NoSaveCamera.svg";
import OffCameraSVG from "./OffCamera.svg";
import SwitchCameraSVG from "./SwitchCamera.svg";
import CustomAlert from "../../../components/CustomAlert/CustomAlert";
import Compressor from 'compressorjs';

const NoSaveCamera = ({ onCapture, onClose, modificatorClass, modificatorClassTextNone, openImmediately = false }) => {
    const [hasPermission, setHasPermission] = useState(false);
    const [permissionStatus, setPermissionStatus] = useState('idle');
    const [currentFacingMode, setCurrentFacingMode] = useState('environment');
    const [stream, setStream] = useState(null);
    const [imageCapture, setImageCapture] = useState(null);
    const [openPopUpCamera, setOpenPopUpCamera] = useState(false);
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [isCompressing, setIsCompressing] = useState(false);
    const [isLowEndDevice, setIsLowEndDevice] = useState(false);

    const [isOpenAlert, setIsOpenAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertTitle, setAlertTitle] = useState('');
    const [alertStatusCod, setAlertStatusCod] = useState('');
    const [alertLoadingGif, setAlertLoadingGif] = useState('');

    const cameraViewRef = useRef(null);
    const photoCanvasRef = useRef(null);
    const permissionRequestedRef = useRef(false);

    // Определение слабого устройства
    useEffect(() => {
        const checkDeviceCapabilities = () => {
            const memory = navigator.deviceMemory || 4;
            const cores = navigator.hardwareConcurrency || 4;
            const isLowMemory = memory <= 2;
            const isLowCPU = cores <= 4;
            const isOldAndroid = /Android [5-8]/.test(navigator.userAgent);

            const isLowEnd = isLowMemory || isLowCPU || isOldAndroid;
            setIsLowEndDevice(isLowEnd);
        };

        checkDeviceCapabilities();
    }, []);

    // Автоматический запрос разрешения
    useEffect(() => {
        if (openImmediately && permissionStatus === 'idle' && !permissionRequestedRef.current) {
            permissionRequestedRef.current = true;
            requestCameraPermission();
        }
    }, [openImmediately, permissionStatus]);

    const handleOpenAlert = (title, message, statusCod, loadingGif) => {
        setAlertTitle(title);
        setAlertMessage(message);
        setAlertStatusCod(statusCod);
        setIsOpenAlert(true);
        setAlertLoadingGif(loadingGif);
    };

    const handleCloseAlert = () => {
        setIsOpenAlert(false);
    };

    const requestCameraPermission = useCallback(async () => {
        setPermissionStatus('requesting');

        try {
            // Проверяем наличие камеры
            const devices = await navigator.mediaDevices.enumerateDevices();
            if (!devices.some(device => device.kind === 'videoinput')) {
                setPermissionStatus('denied');
                handleOpenAlert("Ошибка", "Камера не найдена", "");
                return;
            }

            // Запрашиваем доступ
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment',
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            });

            // Если получили stream - разрешение есть
            stream.getTracks().forEach(track => track.stop());
            setHasPermission(true);
            setPermissionStatus('granted');
            setOpenPopUpCamera(true);

            // Запускаем камеру
            setTimeout(() => startCamera('environment'), 50);

        } catch (error) {
            console.error('Ошибка доступа к камере:', error);

            // Определяем тип ошибки
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                setPermissionStatus('denied');
                // Не показываем алерт автоматически, только если пользователь явно хочет повторить
            } else if (error.name === 'NotFoundError') {
                setPermissionStatus('denied');
                handleOpenAlert("Ошибка", "Камера не найдена", "");
            } else if (error.name === 'NotReadableError') {
                setPermissionStatus('denied');
                handleOpenAlert("Ошибка", "Камера занята другим приложением", "");
            } else {
                setPermissionStatus('denied');
                handleOpenAlert("Ошибка", "Не удалось получить доступ к камере", "");
            }
        }
    }, []);

    const startCamera = useCallback(async (facingMode) => {
        try {
            const constraints = isLowEndDevice ? {
                video: {
                    facingMode: facingMode,
                    width: { ideal: 2560, max: 2560 },
                    height: { ideal: 1440, max: 1440 },
                    frameRate: { ideal: 24, max: 24 }
                }
            } : {
                video: {
                    facingMode: facingMode
                }
            };

            const newStream = await navigator.mediaDevices.getUserMedia(constraints);
            cameraViewRef.current.srcObject = newStream;
            await cameraViewRef.current.play();

            const track = newStream.getVideoTracks()[0];
            setStream(newStream);
            setImageCapture(new ImageCapture(track));
            setCurrentFacingMode(facingMode);
            setIsCameraOn(true);

        } catch (error) {
            try {
                const fallbackStream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: facingMode,
                        width: { ideal: 1920, max: 1920 },
                        height: { ideal: 1080, max: 1080 },
                        frameRate: { ideal: 24, max: 24 }
                    }
                });

                cameraViewRef.current.srcObject = fallbackStream;
                await cameraViewRef.current.play();

                const track = fallbackStream.getVideoTracks()[0];
                setStream(fallbackStream);
                setImageCapture(new ImageCapture(track));
                setCurrentFacingMode(facingMode);
                setIsCameraOn(true);

            } catch (fallbackError) {
                handleOpenAlert("Ошибка", "Не удалось запустить камеру", "");
                setIsCameraOn(false);
            }
        }
    }, [isLowEndDevice]);

    const stopCamera = useCallback(() => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            cameraViewRef.current.srcObject = null;
            setStream(null);
            setImageCapture(null);
            setIsCameraOn(false);
        }
    }, [stream]);

    const switchCamera = useCallback(async () => {
        const newFacingMode = currentFacingMode === 'environment' ? 'user' : 'environment';
        stopCamera();
        await startCamera(newFacingMode);
    }, [currentFacingMode, startCamera, stopCamera]);

    const capturePhoto = useCallback(async () => {
        if (!imageCapture) {
            handleOpenAlert("Ошибка", "Камера не инициализирована", "");
            return;
        }

        try {
            handleOpenAlert("Уведомление", "Делается фото, подождите...", "", "loader");
            setIsCompressing(true);

            if (isLowEndDevice) {
                const bitmap = await imageCapture.grabFrame();
                const canvas = photoCanvasRef.current;
                canvas.width = bitmap.width;
                canvas.height = bitmap.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height);

                canvas.toBlob((originalBlob) => {
                    if (!originalBlob) {
                        handleOpenAlert("Ошибка", "Не удалось создать изображение", "");
                        setIsCompressing(false);
                        return;
                    }

                    new Compressor(originalBlob, {
                        quality: 0.75,
                        maxWidth: 2560,
                        maxHeight: 1920,
                        success: (compressedBlob) => {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                onCapture(reader.result);
                                setIsCompressing(false);
                                handleCloseAlert();
                                closeCamera();
                            };
                            reader.readAsDataURL(compressedBlob);
                        },
                        error: (err) => {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                onCapture(reader.result);
                                setIsCompressing(false);
                                handleCloseAlert();
                                closeCamera();
                            };
                            reader.readAsDataURL(originalBlob);
                        }
                    });
                }, 'image/jpeg', 0.98);
            } else {
                const blob = await imageCapture.takePhoto();

                new Compressor(blob, {
                    quality: 0.75,
                    maxWidth: 5120,
                    maxHeight: 3840,
                    success: (compressedBlob) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            onCapture(reader.result);
                            setIsCompressing(false);
                            handleCloseAlert();
                            closeCamera();
                        };
                        reader.readAsDataURL(compressedBlob);
                    },
                    error: (err) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            onCapture(reader.result);
                            setIsCompressing(false);
                            handleCloseAlert();
                            closeCamera();
                        };
                        reader.readAsDataURL(blob);
                    }
                });
            }
        } catch (error) {
            handleOpenAlert("Ошибка", "Не удалось сделать фото", "");
            setIsCompressing(false);
        }
    }, [imageCapture, onCapture, isLowEndDevice]);

    const closeCamera = useCallback(() => {
        stopCamera();
        setOpenPopUpCamera(false);
        setHasPermission(false);
        setIsCameraOn(false);
        setIsCompressing(false);
        setPermissionStatus('idle');
        permissionRequestedRef.current = false;
        if (onClose) onClose();
    }, [stopCamera, onClose]);

    const retryPermission = useCallback(() => {
        setPermissionStatus('idle');
        permissionRequestedRef.current = false;
        requestCameraPermission();
    }, [requestCameraPermission]);

    const handleToggleCamera = useCallback(() => {
        if (isCameraOn) {
            stopCamera();
        } else {
            startCamera(currentFacingMode);
        }
    }, [isCameraOn, stopCamera, startCamera, currentFacingMode]);

    // Модальное окно запроса разрешения
    if (openImmediately && permissionStatus === 'denied' && openPopUpCamera === false) {
        return (
            <>
                <CustomAlert
                    title={alertTitle}
                    message={alertMessage}
                    statusCod={alertStatusCod}
                    isOpenAlert={isOpenAlert}
                    onClose={handleCloseAlert}
                    classAlertLoading={alertLoadingGif}
                />
                <div className="camera-permission-overlay">
                    <div className="camera-permission-modal">
                        <h3>Доступ к камере запрещён</h3>
                        <p>Чтобы сделать фото, необходимо разрешить доступ к камере</p>
                        <div className="permission-buttons">
                            <button onClick={retryPermission}>
                                Повторить запрос
                            </button>
                            <button onClick={closeCamera}>
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Показываем спиннер во время запроса
    if (openImmediately && permissionStatus === 'requesting') {
        return (
            <div className="camera-permission-overlay">
                <div className="camera-permission-modal">
                    <div className="permission-spinner"></div>
                    <h3>Запрос доступа к камере...</h3>
                </div>
            </div>
        );
    }

    // Обычная кнопка
    if (!hasPermission && !openImmediately) {
        return (
            <>
                <CustomAlert
                    title={alertTitle}
                    message={alertMessage}
                    statusCod={alertStatusCod}
                    isOpenAlert={isOpenAlert}
                    onClose={handleCloseAlert}
                    classAlertLoading={alertLoadingGif}
                />
                <button
                    className={`wrapper__file-upload but_request_permission_camera ${modificatorClass || ''}`}
                    onClick={requestCameraPermission}
                    type="button"
                    disabled={isCompressing}
                >
                    <div className="file-upload but_request_permission_camera-wrapper">
                        <img src={NoSaveCameraSVG} loading="lazy" className="but_request_permission_camera-icons" alt="Иконка камеры" />
                        <h3 className={`file-upload-text ${modificatorClassTextNone || ''}`}>
                            {isCompressing ? "Сжатие..." : "Сделать фото"}
                        </h3>
                    </div>
                </button>
            </>
        );
    }

    // Интерфейс камеры
    return (
        <div>
            <CustomAlert
                title={alertTitle}
                message={alertMessage}
                statusCod={alertStatusCod}
                isOpenAlert={isOpenAlert}
                onClose={handleCloseAlert}
                classAlertLoading={alertLoadingGif}
            />
            <div className="PopUpNoSaveCamera" style={{display: openPopUpCamera ? "flex" : "none"}}>
                <div style={{width:'100%', height:'100%', padding: '1%', position: 'relative'}}>
                    <button
                        onClick={closeCamera}
                        type="button"
                        className="PopUpNoSaveCamera-wrapper-CameraButtons_btnCloseCamera"
                        disabled={isCompressing}
                        aria-label="Закрыть камеру"
                    >
                        +
                    </button>
                    <video
                        ref={cameraViewRef}
                        autoPlay
                        playsInline
                        muted
                        className="PopUpNoSaveCamera-CameraScrean"
                    />
                </div>
                <div className="PopUpNoSaveCamera-wrapper-CameraButtons">
                    <button
                        onClick={switchCamera}
                        type="button"
                        className="PopUpNoSaveCamera-wrapper-CameraButtons_btnSwitch"
                        disabled={!isCameraOn}
                        aria-label="Переключить камеру"
                    >
                        <img src={SwitchCameraSVG} loading="lazy" className="PopUpNoSaveCamera--SVG" alt="Переключить камеру" />
                    </button>
                    <button
                        onClick={capturePhoto}
                        type="button"
                        className="PopUpNoSaveCamera-wrapper-CameraButtons_btnPhoto"
                        disabled={!isCameraOn || isCompressing}
                        aria-label="Сделать фото"
                    >
                        {isCompressing ? (
                            <div className="compressing-spinner" title="Идет сжатие...">⏳</div>
                        ) : (
                            <img src={NoSaveCameraSVG} loading="lazy" className="PopUpNoSaveCamera--SVG" alt="Сделать фото" />
                        )}
                    </button>
                    <div className="toggle-container">
                        <div className="toggle-content">
                            <img src={OffCameraSVG} loading="lazy" className="PopUpNoSaveCamera--SVG" alt="Камера выключена" />
                            <div className="toggle">
                                <input
                                    type="checkbox"
                                    id="camera-switch"
                                    onChange={handleToggleCamera}
                                    checked={isCameraOn}
                                    disabled={isCompressing}
                                />
                                <label htmlFor="camera-switch"></label>
                            </div>
                            <img src={NoSaveCameraSVG} loading="lazy" className="PopUpNoSaveCamera--SVG" alt="Камера включена" />
                        </div>
                        <div>Выкл/Вкл камеру</div>
                    </div>
                </div>
            </div>
            <canvas ref={photoCanvasRef} style={{ display: 'none' }} />
        </div>
    );
};

export default NoSaveCamera;